import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ColorsWidget } from './colorsWidget';
import { Duo } from './duo';

export const ColorsWidgetAdaptive = ({
  onCollapseChange,
  renderCollapsedButton,
}: {
  onCollapseChange?: (isCollapsed: boolean) => void;
  renderCollapsedButton?: (onClick: () => void) => React.ReactNode;
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(1000));
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isCollapsedRef = useRef(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleCollapseChange = useCallback(
    (collapsed: boolean) => {
      isCollapsedRef.current = collapsed;
      setIsCollapsed(collapsed);
      onCollapseChange?.(collapsed);
    },
    [onCollapseChange],
  );

  useEffect(() => {
    if (!containerRef.current || !contentRef.current) {
      return;
    }

    let checkTimeout: NodeJS.Timeout | null = null;
    let isChecking = false;
    let lastCollapsedState: boolean | null = null;

    const checkIfFits = () => {
      // Предотвращаем множественные одновременные проверки
      if (isChecking) {
        return;
      }

      const container = containerRef.current;
      const content = contentRef.current;

      if (!container || !content) {
        return;
      }

      // Проверяем только если легенда видима (не свернута)
      if (isCollapsedRef.current) {
        return;
      }

      // Получаем родительский контейнер для измерения его ширины
      const parentElement = wrapperRef.current?.parentElement || container.parentElement;
      if (!parentElement) {
        return;
      }

      isChecking = true;

      try {
        // ColorsWidget возвращает Box, который является первым дочерним элементом content
        // Элементы легенды находятся внутри этого Box
        const colorsBox = content.firstElementChild as HTMLElement;

        if (!colorsBox) {
          return;
        }

        // Получаем все дочерние элементы Box (элементы легенды)
        const children = Array.from(colorsBox.children) as HTMLElement[];

        if (children.length === 0) {
          return;
        }

        // Получаем позицию и размеры первого элемента относительно родителя
        const firstChild = children[0];
        const firstTop = firstChild.offsetTop;
        const firstHeight = firstChild.offsetHeight;
        const gap = 8; // gridRowGap из ColorsWidget

        // Вычисляем ожидаемую высоту одной строки
        const singleLineHeight = firstHeight + gap;

        // Проверяем, есть ли элементы на второй строке
        // Элемент считается на второй строке, если его offsetTop
        // находится значительно ниже первой строки
        let hasSecondLine = false;
        for (let i = 1; i < children.length; i++) {
          const childTop = children[i].offsetTop;

          // Если элемент находится значительно ниже первого (больше чем высота строки),
          // значит есть перенос
          if (childTop > firstTop + singleLineHeight * 0.5) {
            hasSecondLine = true;
            break;
          }
        }

        // Также проверяем общую высоту контента (Box с легендой) как запасной вариант
        const contentHeight = colorsBox.scrollHeight;
        const fitsInOneLineByHeight = contentHeight <= singleLineHeight + 2;

        // Сворачиваем, если есть вторая строка ИЛИ высота больше одной строки
        const fitsInOneLine = !hasSecondLine && fitsInOneLineByHeight;
        const shouldCollapse = !fitsInOneLine;

        // Обновляем состояние только если оно изменилось
        if (lastCollapsedState !== shouldCollapse) {
          lastCollapsedState = shouldCollapse;
          requestAnimationFrame(() => {
            handleCollapseChange(shouldCollapse);
          });
        }
      } finally {
        isChecking = false;
      }
    };

    // Проверяем сразу с небольшой задержкой для завершения рендеринга
    checkTimeout = setTimeout(checkIfFits, 200);

    // Создаем ResizeObserver для отслеживания изменений размеров
    const resizeObserver = new ResizeObserver(() => {
      // Пропускаем проверку, если идет проверка или легенда свернута
      if (isChecking || isCollapsedRef.current) {
        return;
      }

      // Отменяем предыдущую проверку, если она еще не выполнилась
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }

      // Используем debounce для предотвращения множественных проверок
      checkTimeout = setTimeout(checkIfFits, 300);
    });

    // Отслеживаем изменения размера контейнера
    let retryTimeout: NodeJS.Timeout | null = null;
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    } else {
      // Если wrapperRef еще не установлен, используем setTimeout для повторной попытки
      retryTimeout = setTimeout(() => {
        if (wrapperRef.current) {
          resizeObserver.observe(wrapperRef.current);
        }
      }, 100);
    }

    // Также слушаем resize окна с debounce
    let resizeTimeout: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(checkIfFits, 200);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [handleCollapseChange]);

  const colors = [
    { label: 'Свободен', color: '#181F28' },
    { label: 'Занят', color: '#1CBD91' },
    { label: 'Занят, без заказа', color: '#1CBD91', stripe: '#000000' },
    { label: 'Внимание', color: '#EAAA08' },
    { label: 'Опоздание', color: '#EA2727' },
    { label: 'Требуется действие', color: '#C62828', halfStripe: '#EA2727' },
    { label: 'Есть бронь', dot: '#EA2727' },
  ];

  // Если передан renderCollapsedButton, всегда рендерим свернутую версию
  // (без внутренней логики сворачивания, управляется извне через брейкпоинты)
  if (renderCollapsedButton) {
    return (
      <>
        {renderCollapsedButton(() => setModalOpen(true))}
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          fullWidth
          maxWidth="sm"
          disableRestoreFocus
        >
          <Box className="design-v3-font" padding="24px" display="flex" flexDirection="column">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="20px"
            >
              <Box fontSize={20} fontWeight={600} color="#181F28">
                Что означают цвета столов?
              </Box>
              <IconButton onClick={() => setModalOpen(false)} size="small" style={{ padding: 8 }}>
                <CloseIcon style={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <Box display="flex" flexDirection="column" gridGap="12px">
              {colors.map((color) => {
                const style: React.CSSProperties = {
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  flexShrink: 0,
                };

                if (color.color) {
                  style.background = color.color;
                }

                if (color.stripe) {
                  style.background = `repeating-linear-gradient(
                    -45deg,
                    ${color.stripe} 0 4px,
                    transparent 4px 8px
                  ), ${color.color}`;
                }

                if (color.halfStripe) {
                  style.background = `linear-gradient(
                    135deg,
                    ${color.halfStripe} 50%,
                    ${color.color} 50%
                  )`;
                }

                let element: React.ReactElement;
                if (color.dot) {
                  element = (
                    <Box
                      width={18}
                      height={18}
                      style={style}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box width={6} height={6} borderRadius="50%" bgcolor={color.dot} />
                    </Box>
                  );
                } else {
                  element = <Box width={18} height={18} borderRadius={4} style={style} />;
                }

                return (
                  <Duo
                    key={color.label}
                    first={element}
                    second={
                      <Box fontSize={14} color="rgb(70, 79, 92)">
                        {color.label}
                      </Box>
                    }
                    gridColumnGap={6}
                    alignItems="center"
                  />
                );
              })}
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }

  // Иначе рендерим без renderCollapsedButton
  if (isCollapsed) {
    const helpButton = (
      <ButtonBase
        onClick={() => setModalOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          borderRadius: 6,
          fontSize: 14,
          color: 'rgb(70, 79, 92)',
          cursor: 'pointer',
        }}
      >
        <HelpOutlineIcon style={{ fontSize: 18 }} />
      </ButtonBase>
    );

    const wrappedButton = isDesktop ? (
      <Tooltip
        title="Что означают цвета столов?"
        placement="bottom"
        arrow
        enterDelay={300}
        leaveDelay={100}
      >
        {helpButton}
      </Tooltip>
    ) : (
      helpButton
    );

    return (
      <>
        {wrappedButton}
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          fullWidth
          maxWidth="sm"
          disableRestoreFocus
        >
          <Box className="design-v3-font" padding="24px" display="flex" flexDirection="column">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="20px"
            >
              <Box fontSize={20} fontWeight={600} color="#181F28">
                Что означают цвета столов?
              </Box>
              <IconButton onClick={() => setModalOpen(false)} size="small" style={{ padding: 8 }}>
                <CloseIcon style={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <Box display="flex" flexDirection="column" gridGap="12px">
              {colors.map((color) => {
                const style: React.CSSProperties = {
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  flexShrink: 0,
                };

                if (color.color) {
                  style.background = color.color;
                }

                if (color.stripe) {
                  style.background = `repeating-linear-gradient(
                    -45deg,
                    ${color.stripe} 0 4px,
                    transparent 4px 8px
                  ), ${color.color}`;
                }

                if (color.halfStripe) {
                  style.background = `linear-gradient(
                    135deg,
                    ${color.halfStripe} 50%,
                    ${color.color} 50%
                  )`;
                }

                let element: React.ReactElement;
                if (color.dot) {
                  element = (
                    <Box
                      width={18}
                      height={18}
                      style={style}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box width={6} height={6} borderRadius="50%" bgcolor={color.dot} />
                    </Box>
                  );
                } else {
                  element = <Box width={18} height={18} borderRadius={4} style={style} />;
                }

                return (
                  <Duo
                    key={color.label}
                    first={element}
                    second={
                      <Box fontSize={14} color="rgb(70, 79, 92)">
                        {color.label}
                      </Box>
                    }
                    gridColumnGap={6}
                    alignItems="center"
                  />
                );
              })}
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }

  // Если не свернуто, рендерим полную версию
  return (
    <>
      <div ref={wrapperRef}>
        <div ref={containerRef} style={{ width: '100%', minWidth: 0 }}>
          <div ref={contentRef} style={{ width: '100%' }}>
            <ColorsWidget />
          </div>
        </div>
      </div>
    </>
  );
};
